import { useMemo, useRef } from "preact/hooks";

import { DataTable, type DataTableColumn, type DataTableFilterChip } from "../components/DataTable";
import { FlexibleTaskRow } from "../components/FlexibleTaskRow";
import { TaskRow } from "../components/TaskRow";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useIsSettling } from "../hooks/useSettlingTasks";
import type { GroupKey } from "../hooks/useDue";
import { taskDoneAriaLabel } from "../lib/copy";
import type { DueResponse, DueTask, FlexibleProgress } from "../types";

export interface MyListViewProps {
  data: DueResponse;
  onToggle: (task: DueTask, group: GroupKey) => void;
  onLogFlexible: (item: FlexibleProgress) => void;
}

// Sprint-008 A3 carry-over (design-desktop-phase-a.md §3.5) — My list's
// desktop data-table: the flattened Task/Area/Status/Due view of every
// `DueTask` across `data`'s four groups (overdue/due_today/whenever/
// done_today) — the SAME shape `TeamView.tsx` already established for
// Team, minus the Assignee column (§3.5: "no Assignee — implicit self").
type MyListGroup = "overdue" | "due_today" | "whenever" | "done_today";

interface MyListRow {
  task: DueTask;
  group: MyListGroup;
}

const MY_LIST_GROUP_LABEL: Record<MyListGroup, string> = {
  overdue: "Overdue",
  due_today: "Due today",
  whenever: "Whenever",
  done_today: "Done today",
};

// "Status, then Due asc" (§3.5) — same open-groups-before-done ordering
// `TeamView.tsx` already uses.
const MY_LIST_GROUP_ORDER: Record<MyListGroup, number> = {
  overdue: 0,
  due_today: 1,
  whenever: 2,
  done_today: 3,
};

function flattenMyList(data: DueResponse): MyListRow[] {
  const groups: Array<[MyListGroup, DueTask[]]> = [
    ["overdue", data.overdue],
    ["due_today", data.due_today],
    ["whenever", data.whenever],
    ["done_today", data.done_today],
  ];
  const rows: MyListRow[] = [];
  for (const [group, tasks] of groups) {
    for (const task of tasks) rows.push({ task, group });
  }
  return rows;
}

function dueSortValue(task: DueTask): number {
  return task.due_date ? new Date(task.due_date).getTime() : Number.POSITIVE_INFINITY;
}

function dueDisplayText(task: DueTask): string {
  return task.overdue_phrase ?? task.phrase;
}

/** The desktop table's done toggle. Reads the same post-tap settling
 * state as the phone `TaskRow` (`useSettlingTasks.ts`), so a just-tapped
 * row shows checked during its settle window instead of looking untouched. */
function DoneCell({
  row,
  onToggle,
}: {
  row: MyListRow;
  onToggle: (task: DueTask, group: GroupKey) => void;
}) {
  const settling = useIsSettling(row.task.id);
  const done = row.group === "done_today" || settling;
  return (
    <button
      type="button"
      class="check-circle"
      aria-pressed={done}
      aria-label={taskDoneAriaLabel(row.task.title, done)}
      onClick={() => onToggle(row.task, row.group)}
    >
      {done ? (
        <span class="check-glyph" aria-hidden="true">
          ✓
        </span>
      ) : null}
    </button>
  );
}

/**
 * Sprint-016 F3 (MYL-01, ux-audit-whole-app-2026-07-11.md — S2; UI
 * Designer direction §5, CD-approved) — the leading done column that
 * restores My list's core write-action at `≥1024px`. Identical markup/
 * classes to `TaskRow.tsx`'s phone check-circle (`.check-circle`/
 * `.check-glyph`, same `taskDoneAriaLabel` copy) via `DataTable`'s own
 * `render` extensibility — no change to `DataTable.tsx` itself, so this
 * column is additive/opt-in purely because no other `DataTable` caller
 * (Team/People/Chores/Reports) includes it in their own `columns` array,
 * not because of a flag those callers have to remember to omit.
 */
function myListTableColumns(
  onToggle: (task: DueTask, group: GroupKey) => void,
): Array<DataTableColumn<MyListRow>> {
  return [
    {
      key: "done",
      label: "Done",
      getCsvValue: (row) => (row.group === "done_today" ? "Yes" : "No"),
      render: (row) => <DoneCell row={row} onToggle={onToggle} />,
    },
    {
      key: "task",
      label: "Task",
      sortable: true,
      getSortValue: (row) => row.task.title,
      getCsvValue: (row) => row.task.title,
    },
    {
      key: "area",
      label: "Area",
      sortable: true,
      getSortValue: (row) => row.task.area ?? "",
      getCsvValue: (row) => row.task.area ?? "—",
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      getSortValue: (row) => MY_LIST_GROUP_ORDER[row.group],
      getCsvValue: (row) => MY_LIST_GROUP_LABEL[row.group],
    },
    {
      key: "due",
      label: "Due",
      sortable: true,
      getSortValue: (row) => dueSortValue(row.task),
      getCsvValue: (row) => dueDisplayText(row.task),
    },
  ];
}

const MY_LIST_FILTER_CHIPS: Array<DataTableFilterChip<MyListRow>> = (
  ["overdue", "due_today", "whenever", "done_today"] as MyListGroup[]
).map((group) => ({
  key: group,
  label: MY_LIST_GROUP_LABEL[group],
  predicate: (row: MyListRow) => row.group === group,
}));

function defaultMyListCompare(a: MyListRow, b: MyListRow): number {
  const groupDiff = MY_LIST_GROUP_ORDER[a.group] - MY_LIST_GROUP_ORDER[b.group];
  if (groupDiff !== 0) return groupDiff;
  return dueSortValue(a.task) - dueSortValue(b.task);
}

/**
 * My list = the full personal backlog, due or not (design-spec-today-
 * card.md §3.7 — "a superset of Today's content, not a disjoint list").
 *
 * Sprint-003 A5: `GET /api/v1/due` now scopes to `mine` by default (a
 * real session resolves to a real person), so this collapses back to the
 * spec's actual single flat "Open"/"Done" list — the sprint-002-era
 * per-person grouping (`FLAGGED INTERPRETATION`, since removed) was only
 * ever a stand-in for the missing session; design-qa-sprint-002.md
 * explicitly anticipated it "should collapse away once E4 auth lands."
 *
 * Sprint-008 A3 carry-over (design-desktop-phase-a.md §3.5) — at
 * `≥1024px` the "Open"/"Done" `TaskRow` lists are REPLACED by the shared
 * `DataTable`, flattening all four `data` groups into one sortable/
 * filterable/searchable/exportable table; below 1024px this component
 * renders byte-for-byte what shipped before this sprint. Both
 * presentations read the SAME `data` — no second fetch.
 *
 * The flexible/"aim for" cadences (`data.flexible_progress`) stay their
 * own `FlexibleTaskRow` list at every width, ABOVE the table at desktop —
 * mirroring `TeamView.tsx`'s own precedent exactly ("Handed-out jobs"
 * stays above its table): logging a flexible occurrence is a distinct
 * write interaction that doesn't collapse cleanly into a flat status/due
 * row, and `FlexibleProgress` isn't a `DueTask` in the first place (no
 * single-item "done" state to put in a Status column).
 *
 * Sprint-008 A3's original flagged deviation — "the primary (Task) cell
 * is plain text, not a button... toggling a task's done state from the
 * desktop table is NOT available this Phase A sprint" — is now CLOSED
 * (sprint-016 F3, MYL-01, ux-audit-whole-app-2026-07-11.md's #5 worst-
 * first finding: "the app's single most common action... unavailable at
 * ≥1024px"). The Task cell itself stays plain text (still no
 * `onRowActivate` — this screen still names no per-row "open something"
 * action), but a new leading `done` column now carries the SAME
 * check-circle write-action phone always had — see
 * `myListTableColumns()`'s own docstring for the implementation.
 */
export function MyListView({ data, onToggle, onLogFlexible }: MyListViewProps) {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  // Sprint-008 A2 (R-A0-2) — route-change focus management, same governed
  // convention every other routed view in this sprint reuses (see
  // `TodayView.tsx`'s docstring for the full rationale).
  useFocusHeadingOnMount(headingRef);
  const isDesktop = useIsDesktop();

  const tableColumns = useMemo(() => myListTableColumns(onToggle), [onToggle]);
  const tableRows = useMemo(() => flattenMyList(data), [data]);

  const openGroups: Array<[DueTask[], GroupKey]> = [
    [data.overdue, "overdue"],
    [data.due_today, "due_today"],
    [data.whenever, "whenever"],
  ];
  const openCount =
    openGroups.reduce((sum, [tasks]) => sum + tasks.length, 0) + data.flexible_progress.length;

  return (
    <section class="view" aria-label="My list">
      <h1 ref={headingRef} tabIndex={-1} class="view-heading">
        My list
      </h1>
      <p class="view-subheading">Everything on your plate, due or not.</p>

      {isDesktop ? (
        <>
          {data.flexible_progress.length > 0 ? (
            <>
              <h2 class="section-label flexible">This week</h2>
              <ul class="task-list" style={{ marginBottom: "16px" }}>
                {data.flexible_progress.map((item) => (
                  <FlexibleTaskRow key={item.cadence_id} item={item} onLog={onLogFlexible} />
                ))}
              </ul>
            </>
          ) : null}

          <DataTable
            captionBase="My list"
            columns={tableColumns}
            rows={tableRows}
            getRowId={(row) => `${row.group}-${row.task.id}`}
            loadingMessage="Loading your list…"
            emptyMessage="Nothing here yet today."
            primaryColumnKey="task"
            searchLabel="Search by task title"
            getSearchValue={(row) => row.task.title}
            filterChips={MY_LIST_FILTER_CHIPS}
            filterGroupLabel="Filter by status"
            defaultSortLabel="status ascending"
            defaultCompare={defaultMyListCompare}
            screenSlug="my-list"
          />
        </>
      ) : (
        <>
          <h2 class="section-label due">Open</h2>
          {openCount === 0 ? (
            <p class="empty-hint">Nothing here yet today.</p>
          ) : (
            <ul class="task-list">
              {openGroups.map(([tasks, group]) =>
                tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    done={false}
                    overdue={group === "overdue"}
                    onToggle={(t) => onToggle(t, group)}
                  />
                )),
              )}
              {data.flexible_progress.map((item) => (
                <FlexibleTaskRow key={item.cadence_id} item={item} onLog={onLogFlexible} />
              ))}
            </ul>
          )}

          <h2 class="section-label done">Done</h2>
          {data.done_today.length === 0 ? (
            <p class="empty-hint">Nothing here yet today.</p>
          ) : (
            <ul class="task-list done-container">
              {data.done_today.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  done
                  overdue={false}
                  onToggle={(t) => onToggle(t, "done_today")}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
